// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getFacetData } from "@sc/App/GoogleAnalyticsConnector/Store/Selectors/GoogleAnalyticsSelectors";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { addToEndOfChain } from "@insite/client-framework/HandlerCreator";
import {
    chain,
    DispatchSetFilterQuery,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";

export const GaTrackPLPFilters: typeof DispatchSetFilterQuery = props => {
    const state = props.getState() as xGoogleAnalyticsState;

    if (state.xGoogleAnalytics.plpFilterEvent) {
        const dataLayer = getDataLayer();
        const attributeTypeFacets = getProductListDataViewProperty(state, "attributeTypeFacets");
        const brandFacets = getProductListDataViewProperty(state, "brandFacets");
        const categoryFacets = getProductListDataViewProperty(state, "categoryFacets");
        const productLineFacets = getProductListDataViewProperty(state, "productLineFacets");

        const facet = getFacetData(props, attributeTypeFacets, categoryFacets, productLineFacets, brandFacets);

        if (!dataLayer || !facet.facetTitle) {
            return;
        }

        let attribute = "";
        const {
            attributeValueIds,
            brandIds,
            categoryId,
            productLineIds,
            stockedItemsOnly,
            previouslyPurchasedProducts,
            sort,
        } = props.parameter;

        if (brandIds && brandIds.length > 0) {
            attribute = "brands";
        } else if (categoryId) {
            attribute = "categories";
        } else if (productLineIds && productLineIds.length > 0) {
            attribute = "product_lines";
        } else if (attributeValueIds && attributeValueIds.length > 0) {
            attribute = facet.facetField?.toLowerCase() || "attributes";
        } else if (stockedItemsOnly !== undefined) {
            attribute = "stocked_items";
        } else if (previouslyPurchasedProducts !== undefined) {
            attribute = "previously_purchased";
        } else if (sort) {
            attribute = "sort";
        }

        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.plpFilterEventName,
            page_path: getLocation(state).pathname,
            click_text: facet.facetTitle,
            attribute,
            affiliation: state.xGoogleAnalytics.affiliation || "",
            bill_to_num: state.xGoogleAnalytics.billToNum || "",
            ship_to_num: state.xGoogleAnalytics.shipToNum || "",
            sales_rep: state.xGoogleAnalytics.salesRep || "",
            cust_type: state.xGoogleAnalytics.customerType || "",
            login_type: state.xGoogleAnalytics.loginType || "",
            warehouse: state.xGoogleAnalytics.warehouse || "",
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    }
};

export const GaTrackPLPSearch: typeof DispatchSetFilterQuery = props => {
    const state = props.getState() as xGoogleAnalyticsState;

    if (!state.xGoogleAnalytics.plpSearchEvent) {
        return;
    }

    const searchWithinQueries =
        props.parameter.searchWithinQueries || state.pages.productList.productFilters?.searchWithinQueries;

    if (!searchWithinQueries || searchWithinQueries.length === 0) {
        return;
    }

    const dataLayer = getDataLayer();

    if (!dataLayer) {
        return;
    }

    const searchTerm = Array.isArray(searchWithinQueries) ? searchWithinQueries.join(" ") : searchWithinQueries || "";

    const googleAnalyticsData = {
        event: state.xGoogleAnalytics.plpSearchEventName,
        page_path: getLocation(state).pathname,
        search_term: searchTerm,
        affiliation: state.xGoogleAnalytics.affiliation || "",
        sales_rep: state.xGoogleAnalytics.salesRep || "",
        is_rep: state.xGoogleAnalytics.isRep || "",
        bill_to_num: state.xGoogleAnalytics.billToNum || "",
        ship_to_num: state.xGoogleAnalytics.shipToNum || "",
        cust_type: state.xGoogleAnalytics.customerType || "",
        login_type: state.xGoogleAnalytics.loginType || "",
        warehouse: state.xGoogleAnalytics.warehouse || "",
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(googleAnalyticsData);
};

addToEndOfChain(chain, GaTrackPLPFilters);
addToEndOfChain(chain, GaTrackPLPSearch);
